import React from 'react';
import { useLocation } from 'react-router-dom';
import { Home, Calendar, PlusCircle, Users, UsersRound } from 'lucide-react';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { cn } from '../../constants/designSystem';
import { PrefetchLink } from '../PrefetchLink';

interface MobileBottomNavigationProps {
  className?: string;
  autoHide?: boolean;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navItems: NavItem[] = [
  { label: 'Home', icon: <Home className="h-5 w-5" />, path: '/dashboard' },
  { label: 'Meetups', icon: <Calendar className="h-5 w-5" />, path: '/meetups' },
  { label: 'Create', icon: <PlusCircle className="h-5 w-5" />, path: '/meetups/new' },
  { label: 'Friends', icon: <Users className="h-5 w-5" />, path: '/friends' },
  { label: 'Groups', icon: <UsersRound className="h-5 w-5" />, path: '/groups' },
];

/**
 * Mobile bottom navigation bar
 * Provides thumb-friendly navigation for primary app sections
 * Auto-hides on scroll down, shows on scroll up
 */
function MobileBottomNavigation({
  className,
  autoHide = true,
}: MobileBottomNavigationProps) {
  const location = useLocation();
  const { scrollDirection, isAtTop } = useScrollDirection({ threshold: 20 });

  // Show nav when scrolling up, at top, or auto-hide is disabled
  const showNav = !autoHide || scrollDirection !== 'down' || isAtTop;

  // Determine current route for highlighting
  const getCurrentIndex = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/dashboard')) return 0;
    if (path.startsWith('/meetups') && !path.startsWith('/meetups/new')) return 1;
    if (path.startsWith('/meetups/new')) return 2;
    if (path.startsWith('/friends')) return 3;
    if (path.startsWith('/groups')) return 4;
    return 0;
  };

  const currentIndex = getCurrentIndex();

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ease-out',
        showNav ? 'translate-y-0' : 'translate-y-full',
        className
      )}
    >
      <div
        className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-14">
          {navItems.map((item, index) => {
            const isActive = currentIndex === index;
            return (
              <PrefetchLink
                key={item.path}
                to={item.path}
                className={cn(
                  'flex flex-col items-center justify-center min-w-[60px] py-1 px-2 transition-colors no-underline',
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                )}
              >
                {item.icon}
                <span className="text-xs mt-0.5 font-medium">{item.label}</span>
              </PrefetchLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

// Named export for Vite's code splitting
export { MobileBottomNavigation };
export default MobileBottomNavigation;
